import React from 'react';
import { Building2, Building, Home, Warehouse, Map, Trees, Layers, Store, ChevronDown } from 'lucide-react';

/**
 * The property-type + bed/bath/car (or Floor Area, for Commercial) cluster used
 * when capturing what a buyer is after. Mirrors the full buyer-preferences
 * editor (ContactBuyerPreferencesEditor) so a buyer quick-added here and one
 * edited on the profile page produce identical BuyingPreference data. Added to
 * the Add Buyer modal at CG's request (Monday 13213802286).
 *
 * Controlled: `value` is a slice of the parent form, `onChange(patch)` merges
 * a partial update back. Option lists and property-type ids match the editor
 * exactly so values round-trip through the same buyer-preferences endpoint.
 */
export const PROPERTY_TYPES = [
  { id: 'Apartment', label: 'Apartment', icon: Building2 },
  { id: 'Unit', label: 'Unit', icon: Building2 },
  { id: 'House', label: 'House', icon: Home },
  { id: 'Townhouse', label: 'Townhouse', icon: Building },
  { id: 'Villa', label: 'Villa', icon: Warehouse },
  { id: 'House and Land', label: 'House and Land', icon: Map },
  { id: 'Land', label: 'Land', icon: Trees },
  { id: 'Block of Units', label: 'Block of Units', icon: Layers },
  { id: 'Commercial', label: 'Commercial', icon: Store },
];

// Commercial buyers care about a different spec than residential - the form
// swaps Bedrooms/Bathrooms/Car Spaces for Floor Area when 'Commercial' is chosen.
export const COMMERCIAL_TYPE = 'Commercial';

export const BED_OPTIONS = ['Any', '1', '2', '3', '4', '5', '6+'];
export const BATH_OPTIONS = ['Any', '1+', '2+', '3+', '4+', '5+', '6+'];
export const CAR_OPTIONS = ['Any', '1+', '2+', '3+', '4+', '5+', '6+'];
export const FLOOR_AREA_OPTIONS = ['Any', '50m²', '100m²', '150m²', '200m²', '300m²', '400m²', '500m²', '750m²', '1000m²', '2000m²', '5000m²+'];

export const EMPTY_SPEC = {
  propertyType: [],
  bedroomsMin: 'Any',
  bedroomsMax: 'Any',
  bathrooms: 'Any',
  carSpaces: 'Any',
  floorAreaMin: 'Any',
  floorAreaMax: 'Any',
};

const Dropdown = ({ value, options, onChange }) => {
  // A stored value that isn't one of the fixed steps still needs to show and
  // round-trip, so surface it as its own option.
  const opts = (value && value !== 'Any' && !options.includes(value))
    ? [value, ...options]
    : options;
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-[42px] px-3 rounded-lg border border-gray-300 bg-white text-sm font-medium appearance-none cursor-pointer focus:outline-none focus:border-[#55C5D0] focus:ring-2 focus:ring-[#55C5D0]/10 transition-all"
      >
        {opts.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
    </div>
  );
};

export default function BuyerSpecFields({ value, onChange }) {
  const spec = { ...EMPTY_SPEC, ...value };
  const isCommercial = spec.propertyType.includes(COMMERCIAL_TYPE);

  const togglePropertyType = (id) => {
    const selected = spec.propertyType.includes(id);
    if (id === COMMERCIAL_TYPE) {
      onChange({ propertyType: selected ? [] : [COMMERCIAL_TYPE] });
      return;
    }
    const withoutCommercial = spec.propertyType.filter((v) => v !== COMMERCIAL_TYPE);
    onChange({
      propertyType: selected
        ? withoutCommercial.filter((v) => v !== id)
        : [...withoutCommercial, id],
    });
  };

  return (
    <div className="space-y-4">
      {/* Property Type */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-gray-500 block">Property Type</label>
        <p className="text-[11px] text-gray-400 -mt-1">Select one or more property types</p>
        <div className="grid grid-cols-2 gap-2">
          {PROPERTY_TYPES.map((type) => {
            const isSelected = spec.propertyType.includes(type.id);
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => togglePropertyType(type.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all w-full hover:shadow-sm ${
                  isSelected
                    ? 'bg-[#E0F7FA] border-[#55C5D0] shadow-sm'
                    : 'bg-white border-gray-300 hover:border-[#55C5D0]/50 hover:bg-gray-50'
                }`}
              >
                <div className={`p-1 rounded-full ${isSelected ? 'bg-[#55C5D0]/20 text-[#00838F]' : 'bg-gray-100 text-gray-500'}`}>
                  <Icon size={15} />
                </div>
                <span className={`text-xs font-bold ${isSelected ? 'text-[#00838F]' : 'text-gray-700'}`}>{type.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Commercial -> Floor Area; otherwise Bedrooms / Bathrooms / Car Spaces */}
      {isCommercial ? (
        <div>
          <label className="text-xs font-semibold text-gray-500 mb-1 block">Floor Area</label>
          <div className="grid grid-cols-2 gap-3">
            <Dropdown value={spec.floorAreaMin} options={FLOOR_AREA_OPTIONS} onChange={(v) => onChange({ floorAreaMin: v })} />
            <Dropdown value={spec.floorAreaMax} options={FLOOR_AREA_OPTIONS} onChange={(v) => onChange({ floorAreaMax: v })} />
          </div>
        </div>
      ) : (
        <>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Bedrooms</label>
            <div className="grid grid-cols-2 gap-3">
              <Dropdown value={spec.bedroomsMin} options={BED_OPTIONS} onChange={(v) => onChange({ bedroomsMin: v })} />
              <Dropdown value={spec.bedroomsMax} options={BED_OPTIONS} onChange={(v) => onChange({ bedroomsMax: v })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Bathrooms</label>
              <Dropdown value={spec.bathrooms} options={BATH_OPTIONS} onChange={(v) => onChange({ bathrooms: v })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Car Spaces</label>
              <Dropdown value={spec.carSpaces} options={CAR_OPTIONS} onChange={(v) => onChange({ carSpaces: v })} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
